// src/features/compartilhamento/ShareRewardModal.jsx
//
// Modal que aparece DEPOIS de compartilhar o card (refeição ou treino).
// Dois estados na mesma folha:
//   convite  → agradece, explica a triplicação e oferece "Eu compartilhei"
//   creditado→ confirma o ganho e lembra que vale 1× por dia
//
// O botão é a única coisa que credita: `navigator.share` resolve mesmo quando a
// pessoa cancela a folha do sistema, então quem confirma é ela, não a API.
// O @ do Instagram é opcional e serve só para a verificação manual achar o post
// — fica guardado no aparelho para não pedir de novo todo dia.

import React, { useState } from "react";
import { FiCheck, FiInstagram, FiMessageCircle, FiShare2 } from "react-icons/fi";
import { Spinner } from "react-bootstrap";

import callFirebaseFunction from "../../utils/firebaseFunctionCaller.js";
import { COMPARTILHAMENTO } from "../../config/planoConfig";
import { CANAIS, CANAL_ROTULO, canalDaPista } from "./canaisCompartilhamento";
import { logShareDeclarado } from "../../services/analyticsService";
import Style from "../../styles/features/compartilhamento/ShareReward.module.css";

const INSTAGRAM_KEY = "lesvita.instagram.handle";
const CANAL_KEY = "lesvita.compartilhamento.canal";

/** O que o servidor guarda em `COMPARTILHAMENTOS.TIPO`. */
const TIPO_DO_SHARE = {
  workout: "TREINO",
  meal: "REFEICAO",
  achievement: "CONQUISTA",
};

const OPCOES_CANAL = [
  { id: CANAIS.INSTAGRAM, icone: <FiInstagram aria-hidden="true" /> },
  { id: CANAIS.WHATSAPP, icone: <FiMessageCircle aria-hidden="true" /> },
  { id: CANAIS.OUTRO, icone: <FiShare2 aria-hidden="true" /> },
];

const lerCanalSalvo = () => {
  try {
    const salvo = localStorage.getItem(CANAL_KEY);
    return Object.values(CANAIS).includes(salvo) ? salvo : null;
  } catch {
    return null;
  }
};

const lerHandleSalvo = () => {
  try {
    return localStorage.getItem(INSTAGRAM_KEY) || "";
  } catch {
    return "";
  }
};

const salvarHandle = (valor) => {
  try {
    if (valor) localStorage.setItem(INSTAGRAM_KEY, valor);
  } catch {
    /* storage indisponível — segue sem lembrar */
  }
};

const Premio = ({ valor, label, forte = false }) => (
  <div className={`${Style.premio} ${forte ? Style.premioForte : ""}`}>
    <span className={Style.premioValor}>{valor}</span>
    <span className={Style.premioLabel}>{label}</span>
  </div>
);

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {"meal"|"workout"} props.type  o que foi compartilhado
 * @param {string} [props.origemId]      id da análise/treino (rastro no painel)
 * @param {string} [props.canalHint]     activityType da folha nativa (só pista)
 * @param {Function} props.onClose
 */
const ShareRewardModal = ({ show, type = "meal", origemId = "", canalHint = "", onClose }) => {
  const [instagram, setInstagram] = useState(lerHandleSalvo);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [resultado, setResultado] = useState(null);
  // Canal DECLARADO. Pré-seleção: a pista da folha nativa vence o último canal
  // usado (é informação sobre ESTE compartilhamento); sem as duas, Instagram —
  // é o canal que a campanha pede e o único verificável no painel.
  const [canal, setCanal] = useState(
    () => canalDaPista(canalHint) || lerCanalSalvo() || CANAIS.INSTAGRAM,
  );

  if (!show) return null;

  const ehInstagram = canal === CANAIS.INSTAGRAM;

  const { XP, PONTOS, MULTIPLICADOR_VERIFICADO: MULT } = COMPARTILHAMENTO;
  const xpTriplo = XP * MULT;
  const ptsTriplo = PONTOS * MULT;

  const confirmar = async () => {
    if (enviando) return;
    setEnviando(true);
    setErro("");
    const handle = ehInstagram ? instagram.trim().replace(/^@+/, "") : "";
    try {
      const res = await callFirebaseFunction("registrarCompartilhamento", {
        /* CONQUISTA é um terceiro tipo na fila de verificação: quem revisa
           precisa distinguir "postou o prato" de "postou os 100 dias" — são
           evidências diferentes no Instagram. */
        tipo: TIPO_DO_SHARE[type] || "REFEICAO",
        origemId,
        instagram: handle,
        // Canal escolhido pela pessoa + a pista crua da folha (campos
        // separados: o painel confere o Instagram pelo @, o WhatsApp não tem
        // como ser conferido de fora e é liberado no braço).
        canal,
        canalDetectado: canalHint,
      });
      salvarHandle(handle);
      try { localStorage.setItem(CANAL_KEY, canal); } catch { /* sem storage */ }
      /* O passo que conta: "eu compartilhei" é a única coisa que credita, e
         agora é a única que o funil mede como conversão. A folha aberta é
         medida à parte porque `navigator.share` resolve mesmo cancelado. */
      logShareDeclarado(TIPO_DO_SHARE[type] || "REFEICAO", canal);
      setResultado(res || {});
    } catch (e) {
      setErro("Não conseguimos registrar agora. Tente de novo em instantes.");
      console.error("[compartilhamento] falha ao registrar:", e?.message);
    } finally {
      setEnviando(false);
    }
  };

  const creditado = !!resultado;
  const jaHoje = resultado?.jaRegistradoHoje === true;

  return (
    <div className={Style.overlay} role="dialog" aria-modal="true"
      aria-label="Recompensa por compartilhar">
      <div className={Style.card}>
        <span className={Style.handle} />

        {!creditado ? (
          <>
            <span className={Style.emoji} aria-hidden="true">🧡</span>
            <h2 className={Style.titulo}>Valeu por compartilhar!</h2>
            <p className={Style.texto}>
              Cada post seu tira mais alguém do sofá — é assim que a LesVita
              chega no Brasil inteiro.
            </p>

            <div className={Style.premios}>
              <Premio valor={`+${XP}`} label="XP" />
              <Premio valor={`+${PONTOS}`} label="pontos" />
            </div>

            <div className={Style.campo}>
              <span className={Style.campoLabel}>Onde você compartilhou?</span>
              <div className={Style.canalRow}>
                {OPCOES_CANAL.map((opcao) => (
                  <button
                    key={opcao.id}
                    type="button"
                    className={`${Style.canalBotao} ${canal === opcao.id ? Style.canalBotaoOn : ""}`}
                    onClick={() => setCanal(opcao.id)}
                    aria-pressed={canal === opcao.id}
                  >
                    {opcao.icone}
                    <span>{CANAL_ROTULO[opcao.id]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={Style.verificacao}>
              {ehInstagram ? (
                <>
                  Postou marcando <strong>@lesvita.fit</strong>? A gente confere e{" "}
                  <strong>triplica</strong>: {xpTriplo} XP e {ptsTriplo} pontos.
                </>
              ) : (
                <>
                  A <strong>triplicação</strong> ({xpTriplo} XP e {ptsTriplo} pontos)
                  é conferida no Instagram. Fora dele a gente não consegue ver o
                  post — mas o registro fica salvo e pode ser liberado no suporte.
                </>
              )}
            </div>

            {ehInstagram && (
              <label className={Style.campo}>
                <span className={Style.campoLabel}>
                  Seu @ no Instagram (opcional — ajuda a te achar)
                </span>
                <input
                  className={Style.campoInput}
                  type="text"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="@seuperfil"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                />
              </label>
            )}

            {erro && <p className={Style.erro}>{erro}</p>}

            <div className={Style.acoes}>
              <button type="button" className={Style.botao} onClick={confirmar}
                disabled={enviando}>
                {enviando
                  ? <Spinner animation="border" size="sm" />
                  : <><FiInstagram aria-hidden="true" />Eu compartilhei</>}
              </button>
              <button type="button" className={Style.botaoTexto} onClick={onClose}>
                Agora não
              </button>
            </div>
          </>
        ) : (
          <>
            <span className={Style.emoji} aria-hidden="true">
              {jaHoje ? "👊" : "🎉"}
            </span>
            <h2 className={Style.titulo}>
              {jaHoje ? "Você já garantiu o de hoje" : "Pontos creditados!"}
            </h2>
            <p className={Style.texto}>
              {jaHoje
                ? "A recompensa vale 1× por dia — mas continue compartilhando, cada post ajuda alguém a começar."
                : <>Já estão na sua conta. <strong>Volte amanhã</strong> para ganhar de novo.</>}
            </p>

            {!jaHoje && (
              <div className={Style.premios}>
                <Premio valor={`+${resultado.xp ?? XP}`} label="XP" forte />
                <Premio valor={`+${resultado.pontos ?? PONTOS}`} label="pontos" forte />
              </div>
            )}

            <div className={Style.verificacao}>
              Se você marcou <strong>@lesvita.fit</strong> no post, vamos conferir
              e <strong>triplicar</strong> para {xpTriplo} XP e {ptsTriplo} pontos.
              Avisamos por aqui quando aprovar.
            </div>

            <div className={Style.acoes}>
              <button type="button" className={Style.botao} onClick={onClose}>
                <FiCheck aria-hidden="true" />Entendi
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareRewardModal;
